import { useEffect, useRef, useState } from "react";

const ELECTION_WINDOW_MS = 160;
const HEARTBEAT_INTERVAL_MS = 2000;
const OWNER_TIMEOUT_MS = 6000;
const NAVIGATOR_LOCK_RETRY_MS = 1000;

function createToken() {
  return (typeof window === "undefined" ? null : window.crypto)?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function currentTime() {
  return Date.now();
}

function compareOwners(left, right) {
  const leftSince = Number(left?.ownerSince);
  const rightSince = Number(right?.ownerSince);
  if (leftSince !== rightSince) return leftSince - rightSince;
  return String(left?.token).localeCompare(String(right?.token));
}

// The one per-execution editor/lifecycle-writer key (blueprint §9.4). Every
// local lifecycle writer, the execution screen and the TRAIN hub alike, uses it.
function executionLockName(executionId) {
  return `lz-exec:${executionId}`;
}

const EXECUTION_OPEN_ELSEWHERE = "EXECUTION_OPEN_ELSEWHERE";
const EXECUTION_OPEN_ELSEWHERE_MESSAGE = "This workout is open in another tab";

function executionOpenElsewhereError() {
  const error = new Error(EXECUTION_OPEN_ELSEWHERE_MESSAGE);
  error.code = EXECUTION_OPEN_ELSEWHERE;
  return error;
}

function settleBeforeRelease(beforeRelease, release) {
  if (typeof beforeRelease !== "function") {
    release();
    return;
  }
  Promise.resolve()
    .then(beforeRelease)
    .catch(() => {})
    .then(release);
}

function waitFor(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestNavigatorLock(locks, name, work) {
  let acquired = false;
  const value = await locks.request(name, { ifAvailable: true }, async (lock) => {
    if (!lock) return undefined;
    acquired = true;
    return work();
  });
  return { acquired, value: acquired ? value : undefined };
}

// One election round of the BroadcastChannel fallback protocol used by the
// hook below: probe for a live owner, claim, and hold ownership with heartbeats
// only while `work` runs.
async function withBroadcastExecutionLock(name, work, {
  BroadcastChannelImpl,
  tokenFactory,
  now,
}) {
  const token = tokenFactory();
  const channel = new BroadcastChannelImpl(name);
  let holding = false;
  let ownerSince = null;
  let blocked = false;
  let heartbeatTimer = null;
  const post = (type) => channel.postMessage({ type, token, at: now(), ownerSince });
  channel.onmessage = (event) => {
    const message = event.data || {};
    if (!message.token || message.token === token) return;
    if (holding) {
      if (["PROBE", "CLAIM"].includes(message.type)) post("HEARTBEAT");
      return;
    }
    if (message.type === "HEARTBEAT") blocked = true;
    if (message.type === "CLAIM" && String(message.token) < token) blocked = true;
  };
  try {
    post("PROBE");
    post("CLAIM");
    await waitFor(ELECTION_WINDOW_MS);
    if (blocked) return { acquired: false, value: undefined };
    holding = true;
    ownerSince = now();
    post("HEARTBEAT");
    heartbeatTimer = setInterval(() => post("HEARTBEAT"), HEARTBEAT_INTERVAL_MS);
    return { acquired: true, value: await work() };
  } finally {
    if (heartbeatTimer != null) clearInterval(heartbeatTimer);
    if (holding) post("RELEASE");
    channel.onmessage = null;
    channel.close();
  }
}

/**
 * Runs `work` only while holding the execution's editor lock, without waiting
 * for another holder (ifAvailable). Resolves `{ acquired: false }` when another
 * tab owns the execution; `work` then never runs, so nothing is written.
 */
async function withExecutionLock(executionId, work, {
  navigatorObject = typeof navigator === "undefined" ? null : navigator,
  BroadcastChannelImpl = typeof BroadcastChannel === "undefined"
    ? null
    : BroadcastChannel,
  tokenFactory = createToken,
  now = currentTime,
} = {}) {
  if (!executionId) throw new TypeError("executionId is required");
  const name = executionLockName(executionId);
  if (typeof navigatorObject?.locks?.request === "function") {
    const first = await requestNavigatorLock(navigatorObject.locks, name, work);
    if (first.acquired) return first;
    // An editor that just unmounted in this tab releases its lock
    // asynchronously; give that handoff one task turn before reporting a
    // genuine owner elsewhere.
    await waitFor(0);
    return requestNavigatorLock(navigatorObject.locks, name, work);
  }
  if (!BroadcastChannelImpl) return { acquired: true, value: await work() };
  return withBroadcastExecutionLock(name, work, {
    BroadcastChannelImpl,
    tokenFactory,
    now,
  });
}

export default function useExecutionOwnership(executionId, {
  navigatorObject = typeof navigator === "undefined" ? null : navigator,
  BroadcastChannelImpl = typeof BroadcastChannel === "undefined"
    ? null
    : BroadcastChannel,
  tokenFactory = createToken,
  now = currentTime,
  // Settles work that must land before another tab may take over (the
  // editor's committed-but-unflushed writes). Ownership is released after it.
  beforeRelease = null,
} = {}) {
  const beforeReleaseRef = useRef(beforeRelease);
  useEffect(() => {
    beforeReleaseRef.current = beforeRelease;
  });
  const [ownershipState, setOwnershipState] = useState({
    executionId: null,
    status: "checking",
  });
  const state = ownershipState.executionId === executionId
    ? ownershipState.status
    : "checking";

  useEffect(() => {
    let active = true;
    function updateState(status) {
      if (!active) return;
      setOwnershipState((current) => (
        current.executionId === executionId && current.status === status
          ? current
          : { executionId, status }
      ));
    }

    updateState("checking");
    if (!executionId) return () => {
      active = false;
    };

    const name = executionLockName(executionId);

    if (typeof navigatorObject?.locks?.request === "function") {
      let releaseLock = null;
      let retryTimer = null;
      let requestPending = false;
      let missedAttempts = 0;

      function scheduleRetry(delay) {
        if (!active || retryTimer != null) return;
        retryTimer = setTimeout(() => {
          retryTimer = null;
          requestLock();
        }, delay);
      }

      function handleUnavailableLock() {
        if (!active) return;
        missedAttempts += 1;
        // React StrictMode immediately cleans up and remounts effects in
        // development. The first lock's release settles asynchronously, so a
        // single ifAvailable miss can be this same screen handing ownership to
        // its remount rather than a second tab. Give that handoff one task turn
        // before declaring the screen read-only.
        if (missedAttempts > 1) updateState("readonly");
        scheduleRetry(missedAttempts === 1 ? 0 : NAVIGATOR_LOCK_RETRY_MS);
      }

      function requestLock() {
        if (!active || requestPending || releaseLock) return;
        requestPending = true;

        let request;
        try {
          request = navigatorObject.locks.request(
            name,
            { ifAvailable: true },
            async (lock) => {
              if (!active) return undefined;
              if (!lock) {
                handleUnavailableLock();
                return undefined;
              }

              missedAttempts = 0;
              updateState("owner");
              await new Promise((resolve) => {
                releaseLock = resolve;
              });
              releaseLock = null;
              return undefined;
            }
          );
        } catch (_error) {
          requestPending = false;
          handleUnavailableLock();
          return;
        }

        Promise.resolve(request)
          .catch(handleUnavailableLock)
          .finally(() => {
            requestPending = false;
          });
      }

      requestLock();
      return () => {
        active = false;
        if (retryTimer != null) clearTimeout(retryTimer);
        retryTimer = null;
        const release = releaseLock;
        releaseLock = null;
        if (release) settleBeforeRelease(beforeReleaseRef.current, release);
      };
    }

    if (!BroadcastChannelImpl) {
      updateState("owner");
      return () => {
        active = false;
      };
    }

    const token = tokenFactory();
    const channel = new BroadcastChannelImpl(name);
    let owner = false;
    let ownerSince = null;
    let observedOwner = null;
    let candidates = new Set([token]);
    let electionTimer = null;
    let heartbeatTimer = null;
    let monitorTimer = null;

    function post(type) {
      if (!active) return;
      channel.postMessage({ type, token, at: now(), ownerSince });
    }

    function stopHeartbeat() {
      if (heartbeatTimer != null) clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }

    function cancelElection() {
      if (electionTimer != null) clearTimeout(electionTimer);
      electionTimer = null;
    }

    function beginHeartbeat() {
      cancelElection();
      stopHeartbeat();
      owner = true;
      ownerSince = now();
      observedOwner = null;
      updateState("owner");
      post("HEARTBEAT");
      heartbeatTimer = setInterval(() => post("HEARTBEAT"), HEARTBEAT_INTERVAL_MS);
    }

    function observe(peer) {
      const candidate = { ...peer, lastSeen: now() };
      if (
        !observedOwner ||
        observedOwner.token === candidate.token ||
        compareOwners(candidate, observedOwner) < 0
      ) {
        observedOwner = candidate;
      }
    }

    function demote(peer) {
      owner = false;
      ownerSince = null;
      stopHeartbeat();
      cancelElection();
      observe(peer);
      updateState("readonly");
    }

    function finishElection() {
      electionTimer = null;
      if (!active || owner) return;
      if (observedOwner && now() - observedOwner.lastSeen < OWNER_TIMEOUT_MS) {
        updateState("readonly");
        return;
      }
      const winner = [...candidates].sort()[0];
      if (winner === token) beginHeartbeat();
      else updateState("readonly");
    }

    function startElection() {
      if (!active || owner || electionTimer != null) return;
      observedOwner = null;
      candidates = new Set([token]);
      updateState("checking");
      post("PROBE");
      post("CLAIM");
      electionTimer = setTimeout(finishElection, ELECTION_WINDOW_MS);
    }

    channel.onmessage = (event) => {
      if (!active) return;
      const message = event.data || {};
      if (!message.token || message.token === token) return;

      if (message.type === "PROBE") {
        if (owner) post("HEARTBEAT");
        return;
      }

      if (message.type === "CLAIM") {
        candidates.add(message.token);
        if (owner) post("HEARTBEAT");
        return;
      }

      if (message.type === "HEARTBEAT") {
        const peer = {
          token: message.token,
          ownerSince: Number(message.ownerSince ?? message.at),
        };
        if (owner) {
          const self = { token, ownerSince };
          if (compareOwners(peer, self) < 0) demote(peer);
          else post("HEARTBEAT");
        } else {
          cancelElection();
          observe(peer);
          updateState("readonly");
        }
        return;
      }

      if (message.type === "RELEASE" && observedOwner?.token === message.token) {
        observedOwner = null;
        startElection();
      }
    };

    monitorTimer = setInterval(() => {
      if (
        active && !owner && electionTimer == null &&
        (!observedOwner || now() - observedOwner.lastSeen >= OWNER_TIMEOUT_MS)
      ) {
        startElection();
      }
    }, HEARTBEAT_INTERVAL_MS);
    startElection();

    return () => {
      const wasOwner = owner;
      active = false;
      cancelElection();
      stopHeartbeat();
      if (monitorTimer != null) clearInterval(monitorTimer);
      channel.onmessage = null;
      const release = () => {
        if (wasOwner) channel.postMessage({ type: "RELEASE", token, at: now(), ownerSince });
        channel.close();
      };
      if (wasOwner) settleBeforeRelease(beforeReleaseRef.current, release);
      else release();
    };
  }, [BroadcastChannelImpl, executionId, navigatorObject, now, tokenFactory]);

  return {
    checking: state === "checking",
    isOwner: state === "owner",
    readOnly: state === "readonly",
  };
}

export {
  ELECTION_WINDOW_MS,
  EXECUTION_OPEN_ELSEWHERE,
  EXECUTION_OPEN_ELSEWHERE_MESSAGE,
  HEARTBEAT_INTERVAL_MS,
  NAVIGATOR_LOCK_RETRY_MS,
  OWNER_TIMEOUT_MS,
  compareOwners,
  executionLockName,
  executionOpenElsewhereError,
  withExecutionLock,
};
